import { computed, inject, Signal } from "@angular/core";
import { DataAccess } from "../dataaccess/data.access";
import { S25Util } from "../util/s25-util";

import {
    WaitlistEntry,
    WaitlistCreatRequest,
    WaitlistQueryParams,
    WaitlistReorderRequest,
    Waitlists,
    WaitlistsReorderRequest,
    WaitlistUrlParams,
    WaitlistListQueryParams,
    WaitlistEventQueryParams,
    WaitlistPostGetListBody,
    WaitlistPostGetEventBody,
    WaitlistRequest,
    WaitlistRequestFilter,
    WaitingEntryRequest,
    WaitingEntry,
} from "../pojo/Waitlist";

import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { PreferenceService } from "../services/preference.service";
import { LoginStore } from "./login.store";
interface WaitlistState {
    waitlistCache: Map<string, WaitlistEntry[]>;
    loadingStates: Set<string>;
    permissions: Map<number, { canSee: boolean; canUse: boolean }>;
}

const initialState: WaitlistState = {
    waitlistCache: new Map(),
    loadingStates: new Set(),
    permissions: new Map(),
};

function isEventQuery(params: WaitlistQueryParams): params is WaitlistEventQueryParams {
    return "event_id" in params && params.event_id != null;
}

function isDetailsOnlyQuery(params: WaitlistQueryParams): boolean {
    if (!("waitlist_id" in params) || params.waitlist_id == null) return false;
    const p = params as WaitlistListQueryParams;
    return p.rsrv_id == null && p.space_id == null && p.start_dt == null && p.end_dt == null && !isEventQuery(params);
}

export const WaitlistStore = signalStore(
    { providedIn: "root" },

    withState(initialState),

    withProps((store) => ({
        _loginStore: inject(LoginStore),
    })),

    withComputed((store) => ({
        allWaitlistEntries: computed(() => {
            const result: WaitlistEntry[] = [];
            store.waitlistCache().forEach((list) => result.push(...list));
            return result;
        }),
    })),

    withMethods((store) => {
        /** Coalesce concurrent getWaitlistEntriesByRsrvIds fetches for the same rsrv id set. */
        const rsrvBatchInflight = new Map<string, Promise<void>>();

        let globalWaitlistEnabledPromise: Promise<boolean> | undefined;

        const isWaitlistGloballyEnabled = (): Promise<boolean> => {
            if (globalWaitlistEnabledPromise === undefined) {
                globalWaitlistEnabledPromise = PreferenceService.getPreferences(["25L_sp_waitlist"]).then((pref) =>
                    S25Util.toBool(pref?.["25L_sp_waitlist"]?.value),
                );
            }
            return globalWaitlistEnabledPromise;
        };

        const getCacheKey = (params: WaitlistQueryParams): string => {
            if (isEventQuery(params)) {
                return `event:${params.event_id}:${params.space_id ?? ""}:${params.include_requests ?? "formal"}`;
            }
            if (isDetailsOnlyQuery(params)) {
                const p = params as WaitlistListQueryParams;
                return `waitlist:${params.waitlist_id}:${p.include_requests ?? "formal"}`;
            }
            const p = params as WaitlistListQueryParams;
            if (p.rsrv_id != null) {
                return `rsrv:${p.rsrv_id}:${p.waitlist_id ?? ""}:${p.blocking_rsrv_id ?? ""}:${p.include_requests ?? ""}`;
            }
            if (p.space_id != null && p.start_dt && p.end_dt) {
                return `space:${p.space_id}:${S25Util.date.toS25ISODateTimeStr(p.start_dt)}:${S25Util.date.toS25ISODateTimeStr(p.end_dt)}:${p.include_requests ?? ""}:${p.waitlist_id ?? ""}:${p.blocking_rsrv_id ?? ""}`;
            }
            return `misc:${JSON.stringify(params)}`;
        };

        const buildUrlParams = (params: WaitlistQueryParams): WaitlistUrlParams => {
            const p: WaitlistUrlParams = {};
            if (isEventQuery(params)) {
                p.event_id = params.event_id;
                if (params.space_id != null) p.space_id = params.space_id;
                if (params.include_requests) p.include_requests = params.include_requests;
                return p;
            }
            const list = params as WaitlistListQueryParams;
            if (list.waitlist_id != null) p.waitlist_id = list.waitlist_id;
            if (list.space_id != null) p.space_id = list.space_id;
            if (list.rsrv_id != null) p.rsrv_id = list.rsrv_id;
            if (list.blocking_rsrv_id != null) p.blocking_rsrv_id = list.blocking_rsrv_id;
            if (list.start_dt) p.start_dt = S25Util.date.toS25ISODateTimeStr(list.start_dt)!;
            if (list.end_dt) p.end_dt = S25Util.date.toS25ISODateTimeStr(list.end_dt)!;
            if (list.include_requests) p.include_requests = list.include_requests;
            return p;
        };

        const extractError = (error: any, fallback: string): string => {
            if (error?.data?.message) return error.data.message;
            if (error?.data?.error) return error.data.error + (error.data.details ? `: ${error.data.details}` : "");
            if (error?.message) return error.message;
            return S25Util.errorText(error, fallback);
        };

        const setLoading = (key: string, value: boolean) => {
            patchState(store, {
                loadingStates: (() => {
                    const next = new Set(store.loadingStates());
                    value ? next.add(key) : next.delete(key);
                    return next;
                })(),
            });
        };

        const updateCache = (key: string, entries: WaitlistEntry[]) => {
            patchState(store, {
                waitlistCache: (() => {
                    const next = new Map(store.waitlistCache());
                    next.set(key, entries);
                    return next;
                })(),
            });
        };

        const invalidateCache = (entry: WaitlistEntry) => {
            patchState(store, {
                waitlistCache: (() => {
                    const next = new Map(store.waitlistCache());
                    if (entry.blockingRsrvId) next.delete(`blockingRsrv:${entry.blockingRsrvId}`);
                    for (const w of entry.waiting ?? []) if (w.rsrvId) next.delete(`rsrv:${w.rsrvId}`);
                    if (entry.spaceId) {
                        for (const [k] of next) {
                            if (k.startsWith(`space:${entry.spaceId}:`)) next.delete(k);
                        }
                    }
                    if (entry.blockingEventId) {
                        for (const [k] of next) {
                            if (k.startsWith(`event:${entry.blockingEventId}:`)) next.delete(k);
                        }
                    }
                    return next;
                })(),
            });
        };

        const waitlistBySpace = (spaceId: number) =>
            computed(() => {
                for (const [key, list] of store.waitlistCache()) {
                    if (key.startsWith(`space:${spaceId}:`)) return list;
                }
                return [];
            });

        const waitlistById = (waitlistId: number) =>
            computed(() => {
                const prefix = `waitlist:${waitlistId}:`;
                for (const [k, v] of store.waitlistCache()) {
                    if (k.startsWith(prefix)) return v;
                }
                return [];
            });

        /** Groups returned from GET list.json when filtered by rsrv_id. */
        const waitlistByRsrv = (rsrvId: number): Signal<WaitlistEntry[]> =>
            computed(() => {
                const prefix = `rsrv:${rsrvId}:`;
                for (const [k, v] of store.waitlistCache()) {
                    if (k.startsWith(prefix)) return v;
                }
                return [];
            });

        /** GET /waitlist/event.json — full list for an event (same cache for blocking vs participant views). */
        const waitlistForEvent = (eventId: number, includeFilter: string = "all_competes") =>
            computed(() => store.waitlistCache().get(`event:${eventId}::${includeFilter}`) ?? []);

        const isLoading = (key: string) => computed(() => store.loadingStates().has(key));

        const canSeeWaitlistSignal = (spaceId: number) =>
            computed(() => store.permissions().get(spaceId)?.canSee ?? true);

        const canUseWaitlistSignal = (spaceId: number) =>
            computed(() => store.permissions().get(spaceId)?.canUse ?? true);

        const buildPostListJsonBody = (body: WaitlistPostGetListBody): Record<string, unknown> => {
            const out: Record<string, unknown> = {};
            if (body.waitlist_id != null) out.waitlist_id = body.waitlist_id;
            if (body.space_id != null && String(body.space_id).trim() !== "") {
                out.space_id = String(body.space_id).trim();
            }
            if (body.rsrv_id != null) out.rsrv_id = body.rsrv_id;
            if (body.blocking_rsrv_id != null) out.blocking_rsrv_id = body.blocking_rsrv_id;
            if (body.start_dt != null && body.start_dt !== "") {
                out.start_dt = S25Util.date.toS25ISODateTimeStr(body.start_dt as any) ?? String(body.start_dt);
            }
            if (body.end_dt != null && body.end_dt !== "") {
                out.end_dt = S25Util.date.toS25ISODateTimeStr(body.end_dt as any) ?? String(body.end_dt);
            }
            if (body.include_requests) out.include_requests = body.include_requests;
            return out;
        };

        const getCacheKeyForPostListBody = (body: WaitlistPostGetListBody): string => {
            const inc = body.include_requests ?? "formal";
            if (body.rsrv_id != null) {
                return `rsrv:${body.rsrv_id}:${body.waitlist_id ?? ""}:${body.blocking_rsrv_id ?? ""}:${inc}`;
            }
            const space = body.space_id != null ? String(body.space_id).trim() : "";
            const start =
                body.start_dt != null
                    ? (S25Util.date.toS25ISODateTimeStr(body.start_dt as any) ?? String(body.start_dt))
                    : "";
            const end =
                body.end_dt != null
                    ? (S25Util.date.toS25ISODateTimeStr(body.end_dt as any) ?? String(body.end_dt))
                    : "";
            if (space && start && end) {
                return `space:${space}:${start}:${end}:${inc}:${body.waitlist_id ?? ""}:${body.blocking_rsrv_id ?? ""}`;
            }
            return `post:${JSON.stringify(buildPostListJsonBody(body))}`;
        };

        /** POST /waitlist/list.json?request_method=get — same response as GET list; for long {@code space_id} strings. */
        const getWaitlistEntriesPost = async (
            body: WaitlistPostGetListBody,
            force = false,
        ): Promise<WaitlistEntry[]> => {
            if (!store._loginStore.loggedIn()) return [];
            if (!(await isWaitlistGloballyEnabled())) return [];

            const key = getCacheKeyForPostListBody(body);
            if (!force && store.waitlistCache().has(key)) return store.waitlistCache().get(key)!;

            setLoading(key, true);
            try {
                const payload = { post_get: buildPostListJsonBody(body) };
                const response = await DataAccess.post<Waitlists>(
                    DataAccess.injectCaller(
                        "/waitlist/list.json?request_method=get",
                        "WaitlistStore.getWaitlistEntriesPost",
                    ),
                    payload,
                );
                const entries = response?.waitlists ?? [];
                updateCache(key, entries);
                return entries;
            } finally {
                setLoading(key, false);
            }
        };

        const getWaitlistEntries = async (
            params: WaitlistQueryParams = {},
            force = false,
        ): Promise<WaitlistEntry[]> => {
            if (!store._loginStore.loggedIn()) return [];
            if (!(await isWaitlistGloballyEnabled())) return [];

            const key = getCacheKey(params);
            if (!force && store.waitlistCache().has(key)) return store.waitlistCache().get(key)!;

            setLoading(key, true);

            try {
                const qs = DataAccess.params(buildUrlParams(params));
                if (isEventQuery(params)) {
                    const url = `/waitlist/event.json${qs ? `?${qs}` : ""}`;
                    const response = await DataAccess.get<Waitlists>(
                        DataAccess.injectCaller(url, "WaitlistStore.getWaitlistEntries.event"),
                    );
                    const entries = response?.waitlists ?? [];
                    updateCache(key, entries);
                    return entries;
                }
                if (isDetailsOnlyQuery(params)) {
                    const url = `/waitlist/details.json${qs ? `?${qs}` : ""}`;
                    const group = await DataAccess.get<WaitlistEntry>(
                        DataAccess.injectCaller(url, "WaitlistStore.getWaitlistEntries.details"),
                    );
                    const entries = group ? [group] : [];
                    updateCache(key, entries);
                    return entries;
                }
                const url = `/waitlist/list.json${qs ? `?${qs}` : ""}`;
                const response = await DataAccess.get<Waitlists>(
                    DataAccess.injectCaller(url, "WaitlistStore.getWaitlistEntries.list"),
                );
                const entries = response?.waitlists ?? [];
                updateCache(key, entries);
                return entries;
            } finally {
                setLoading(key, false);
            }
        };

        const waitingEntriesToCreateRequests = (waiting: WaitingEntry[]): WaitingEntryRequest[] =>
            [...waiting]
                .sort((a, b) => a.position - b.position)
                .map((w) => ({
                    rsrvId: w.rsrvId,
                    position: w.position,
                    conflictStartDt: w.startDt,
                    conflictEndDt: w.endDt,
                }));

        /** POST /waitlist/list.json — builds {@link WaitlistCreatRequest} from a UI waitlist group (e.g. save order before the group has a {@link WaitlistEntry.waitlistId}). */
        const createWaitlist = (spaceId: number, group: WaitlistEntry): Promise<WaitlistEntry> => {
            const waitlist: WaitlistRequest = {
                spaceId,
                ...(group.blockingRsrvId ? { blockingRsrvId: group.blockingRsrvId } : {}),
                ...(group.blockingEventId ? { blockingEventId: group.blockingEventId } : {}),
                waiting: waitingEntriesToCreateRequests(group.waiting ?? []),
            };
            return createWaitlistEntry({ request: { waitlists: [waitlist] } });
        };

        const createWaitlistEntry = async (entry: WaitlistCreatRequest): Promise<WaitlistEntry> => {
            try {
                const res = await DataAccess.post<Waitlists>(
                    DataAccess.injectCaller("/waitlist/list.json", "WaitlistStore.createWaitlistEntry"),
                    entry,
                );
                const created = res?.waitlists?.[0];
                if (!created) {
                    const err = res?.errors?.[0] as any;
                    throw new Error(
                        err?.message || err?.error || extractError(null, "No waitlist returned from create"),
                    );
                }
                invalidateCache(created);
                return created;
            } catch (e) {
                throw new Error(extractError(e, "Failed to create waitlist entry"));
            }
        };

        const createWaitlistEntriesBatch = async (groups: WaitlistRequest[]) => {
            try {
                const res = await DataAccess.post<Waitlists>(
                    DataAccess.injectCaller("/waitlist/list.json", "WaitlistStore.createWaitlistEntriesBatch"),
                    { request: { waitlists: groups } },
                );
                res.waitlists?.forEach((e) => invalidateCache(e));
                return {
                    waitlist: res.waitlists ?? [],
                    errors: res.errors?.map((e: any, i: number) => ({
                        index: i,
                        message: e?.message || e?.error || "Unknown error",
                    })),
                };
            } catch (e) {
                throw new Error(extractError(e, "Batch creation failed"));
            }
        };

        const reorderWaitlist = async (request: WaitlistReorderRequest): Promise<void> => {
            try {
                const body: WaitlistsReorderRequest = { reorder: { waitlists: [request] } };
                await DataAccess.post(
                    DataAccess.injectCaller("/waitlist/reorder.json", "WaitlistStore.reorderWaitlist"),
                    body,
                );
                patchState(store, { waitlistCache: new Map() });
            } catch (e) {
                throw new Error(extractError(e, "Failed to reorder waitlist"));
            }
        };

        const deleteWaitlistEntry = async (
            waitlistId: number,
            entry?: Partial<WaitlistEntry>,
            rsrvId?: number,
        ): Promise<void> => {
            try {
                if (rsrvId == null) {
                    throw new Error("rsrv_id is required to delete a waitlist row");
                }
                await DataAccess.delete(
                    DataAccess.injectCaller(
                        `/waitlist/details.json?waitlist_id=${waitlistId}&rsrv_id=${rsrvId}`,
                        "WaitlistStore.deleteWaitlistEntry",
                    ),
                );
                if (entry) invalidateCache(entry as WaitlistEntry);
                else patchState(store, { waitlistCache: new Map() });
            } catch (e) {
                throw new Error(extractError(e, "Failed to delete waitlist entry"));
            }
        };

        /** POST /waitlist/event.json — batch fetch waitlist entries for multiple event IDs in a single call. */
        const getWaitlistEntriesByEventIds = async (
            eventIds: number[],
            includeRequests: WaitlistRequestFilter = "all_competes",
            force = false,
        ): Promise<Map<number, WaitlistEntry[]>> => {
            if (!store._loginStore.loggedIn() || !eventIds?.length) return new Map();
            if (!(await isWaitlistGloballyEnabled())) {
                const empty = new Map<number, WaitlistEntry[]>();
                eventIds.forEach((eventId) => empty.set(eventId, []));
                return empty;
            }

            const uncachedIds = force
                ? eventIds
                : eventIds.filter((id) => !store.waitlistCache().has(`event:${id}::${includeRequests}`));

            if (uncachedIds.length > 0) {
                const loadingKey = `event-batch:${uncachedIds.join("+")}`;
                setLoading(loadingKey, true);
                try {
                    const payload = {
                        post_get: {
                            event_id: uncachedIds.join("+"),
                            include_requests: includeRequests,
                        },
                    };
                    const response = await DataAccess.post<Waitlists>(
                        DataAccess.injectCaller(
                            "/waitlist/event.json?request_method=get",
                            "WaitlistStore.getWaitlistEntriesByEventIds",
                        ),
                        payload,
                    );
                    const entries = response?.waitlists ?? [];

                    const grouped = new Map<number, WaitlistEntry[]>();
                    uncachedIds.forEach((id) => grouped.set(id, []));
                    for (const entry of entries) {
                        grouped.get(entry.blockingEventId)?.push(entry);
                    }

                    grouped.forEach((list, eventId) => {
                        updateCache(`event:${eventId}::${includeRequests}`, list);
                    });
                } finally {
                    setLoading(loadingKey, false);
                }
            }

            const result = new Map<number, WaitlistEntry[]>();
            eventIds.forEach((id) => {
                result.set(id, store.waitlistCache().get(`event:${id}::${includeRequests}`) ?? []);
            });
            return result;
        };

        /** POST /waitlist/list.json?request_method=get — batch fetch waitlist entries for multiple rsrv IDs in a single call. */
        const getWaitlistEntriesByRsrvIds = async (
            rsrvIds: number[],
            includeRequests: WaitlistRequestFilter = "all_competes",
            force = false,
        ): Promise<WaitlistEntry[]> => {
            if (!store._loginStore.loggedIn() || !rsrvIds?.length) return [];
            if (!(await isWaitlistGloballyEnabled())) return [];

            const uniqueIds = S25Util.array.unique(rsrvIds);
            const uncachedIds = force
                ? uniqueIds
                : uniqueIds.filter((id) => !store.waitlistCache().has(`rsrv:${id}:::${includeRequests}`));

            if (uncachedIds.length > 0) {
                const sortedKey = uncachedIds
                    .slice()
                    .sort((a, b) => a - b)
                    .join("+");
                const inflightKey = `${includeRequests}::${sortedKey}`;
                let batchPromise = rsrvBatchInflight.get(inflightKey);
                if (!batchPromise) {
                    const loadingKey = `rsrv-batch:${sortedKey}`;
                    batchPromise = (async () => {
                        setLoading(loadingKey, true);
                        try {
                            const payload = {
                                post_get: {
                                    rsrv_id: uncachedIds.join("+"),
                                    include_requests: includeRequests,
                                },
                            };
                            const response = await DataAccess.post<Waitlists>(
                                DataAccess.injectCaller(
                                    "/waitlist/list.json?request_method=get",
                                    "WaitlistStore.getWaitlistEntriesByRsrvIds",
                                ),
                                payload,
                            );
                            const entries = response?.waitlists ?? [];

                            const grouped = new Map<number, WaitlistEntry[]>();
                            uncachedIds.forEach((id) => grouped.set(id, []));
                            for (const entry of entries) {
                                for (const w of entry.waiting ?? []) {
                                    if (grouped.has(w.rsrvId)) {
                                        grouped.get(w.rsrvId)!.push(entry);
                                    }
                                }
                            }

                            grouped.forEach((list, rsrvId) => {
                                updateCache(`rsrv:${rsrvId}:::${includeRequests}`, list);
                            });
                        } finally {
                            setLoading(loadingKey, false);
                            rsrvBatchInflight.delete(inflightKey);
                        }
                    })();
                    rsrvBatchInflight.set(inflightKey, batchPromise);
                }
                await batchPromise;
            }

            const seen = new Set<string>();
            const result: WaitlistEntry[] = [];
            for (const id of uniqueIds) {
                for (const entry of store.waitlistCache().get(`rsrv:${id}:::${includeRequests}`) ?? []) {
                    const key = `${entry.waitlistId}:${entry.groupKey}`;
                    if (!seen.has(key)) {
                        seen.add(key);
                        result.push(entry);
                    }
                }
            }
            return result;
        };

        const clearCache = () => {
            patchState(store, { waitlistCache: new Map() });
        };

        const clearPermissions = () => {
            patchState(store, { permissions: new Map() });
        };

        return {
            waitlistBySpace,
            waitlistById,
            waitlistByRsrv,
            waitlistByEvent: waitlistForEvent,
            waitlistByEventId: waitlistForEvent,
            isLoading,
            canSeeWaitlistSignal,
            canUseWaitlistSignal,
            getWaitlistEntries,
            getWaitlistEntriesPost,
            getWaitlistEntriesByEventIds,
            getWaitlistEntriesByRsrvIds,
            createWaitlist,
            createWaitlistEntry,
            createWaitlistEntriesBatch,
            deleteWaitlistEntry,
            reorderWaitlist,
            clearCache,
            clearPermissions,
        };
    }),
);

export type WaitlistStoreType = typeof WaitlistStore;
